import React, { useState, useEffect } from 'react';
import { db, appId as _appId } from './contexts/AuthContext';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

function shortlistDocRef(token) {
  return doc(db, 'artifacts', _appId, 'public', 'data', 'shortlists', token);
}

function feedbackDocRef(token, candidateId) {
  return doc(db, 'artifacts', _appId, 'public', 'data', 'shortlists', token, 'feedback', candidateId);
}

function FitRing({ score }) {
  const r = 20;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, Number(score) || 0));
  const color = pct >= 75 ? '#34d399' : pct >= 50 ? '#fbbf24' : '#f87171';
  return (
    <svg width={52} height={52} viewBox="0 0 52 52" className="shrink-0">
      <circle cx="26" cy="26" r={r} fill="none" stroke="#1e293b" strokeWidth="4" />
      <circle
        cx="26" cy="26" r={r} fill="none"
        stroke={color} strokeWidth="4"
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - pct / 100)}
        strokeLinecap="round"
        transform="rotate(-90 26 26)"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x="26" y="30" textAnchor="middle" fontSize="11" fontWeight="900" fill="white">{pct}%</text>
    </svg>
  );
}

function CandidateCard({ candidate, feedback, onFeedback }) {
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const verdict = feedback?.verdict;

  const handleVote = async (v) => {
    setSaving(true);
    await onFeedback(candidate.id, { verdict: v, comment: comment || '' });
    setSaving(false);
    setShowComment(false);
  };

  const initials = (candidate.name || '??')
    .split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="rounded-shell border border-white/10 bg-slate-900/80 p-6 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 shrink-0 rounded-card bg-slate-800 flex items-center justify-center text-base font-black text-cyan-300">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-base font-black text-white truncate">{candidate.name}</div>
          <div className="text-xs text-cyan-300/80 truncate mt-0.5">{candidate.role || 'Role not specified'}</div>
          {candidate.location && <div className="text-micro text-slate-500 mt-0.5">{candidate.location}</div>}
        </div>
        <FitRing score={candidate.rating} />
      </div>

      {/* Summary */}
      {candidate.summary && (
        <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{candidate.summary}</p>
      )}

      {/* Skills */}
      {candidate.skills && candidate.skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {candidate.skills.slice(0, 6).map((s, i) => (
            <span key={i} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-micro font-bold text-slate-300">
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Feedback */}
      <div className="mt-auto pt-2 border-t border-white/10">
        {verdict ? (
          <div className={`flex items-center gap-2 text-xs font-bold ${verdict === 'yes' ? 'text-emerald-400' : verdict === 'no' ? 'text-rose-400' : 'text-amber-400'}`}>
            <span className="text-lg">{verdict === 'yes' ? '👍' : verdict === 'no' ? '👎' : '🤔'}</span>
            {verdict === 'yes' ? 'Interested' : verdict === 'no' ? 'Not a fit' : 'Maybe'}
            {feedback?.comment && <span className="ml-2 text-slate-400 font-normal">· {feedback.comment}</span>}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => handleVote('yes')}
                className="flex-1 rounded-ctl border border-emerald-400/30 bg-emerald-500/10 py-2 text-meta font-black uppercase tracking-[0.12em] text-emerald-300 transition hover:bg-emerald-500/20 disabled:opacity-40"
              >
                👍 Yes
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => handleVote('maybe')}
                className="flex-1 rounded-ctl border border-amber-400/30 bg-amber-500/10 py-2 text-meta font-black uppercase tracking-[0.12em] text-amber-300 transition hover:bg-amber-500/20 disabled:opacity-40"
              >
                🤔 Maybe
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => handleVote('no')}
                className="flex-1 rounded-ctl border border-rose-400/30 bg-rose-500/10 py-2 text-meta font-black uppercase tracking-[0.12em] text-rose-300 transition hover:bg-rose-500/20 disabled:opacity-40"
              >
                👎 No
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowComment((v) => !v)}
              className="text-micro text-slate-500 underline underline-offset-2 hover:text-slate-300 transition"
            >
              {showComment ? 'Hide comment' : '+ Add a comment'}
            </button>
            {showComment && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional note…"
                  className="flex-1 rounded-ctl border border-white/10 bg-slate-800 px-3 py-2 text-xs text-white placeholder-slate-600 focus:border-cyan-500/40 focus:outline-none"
                  onKeyDown={(e) => { if (e.key === 'Enter' && comment) handleVote('yes'); }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ShortlistPortal({ token, onExit }) {
  const [loading, setLoading] = useState(true);
  const [shortlist, setShortlist] = useState(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState({});
  const [allDone, setAllDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const snap = await getDoc(shortlistDocRef(token));
        if (!snap.exists()) {
          setError('This shortlist link is not valid or has expired.');
        } else {
          const data = snap.data();
          setShortlist(data);
          // Load existing feedback
          const fb = {};
          await Promise.all((data.candidates || []).map(async (c) => {
            const fbSnap = await getDoc(feedbackDocRef(token, c.id));
            if (fbSnap.exists()) fb[c.id] = fbSnap.data();
          }));
          setFeedback(fb);
        }
      } catch (err) {
        setError('Failed to load shortlist. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleFeedback = async (candidateId, data) => {
    try {
      await setDoc(feedbackDocRef(token, candidateId), {
        ...data,
        candidateId,
        token,
        submittedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setFeedback((prev) => ({ ...prev, [candidateId]: data }));
      // Check if all candidates have feedback
      const total = shortlist?.candidates?.length || 0;
      const done = Object.keys({ ...feedback, [candidateId]: data }).length;
      if (done >= total) setAllDone(true);
    } catch (err) {
      console.error('Failed to save feedback', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400 text-sm">Loading shortlist…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-4xl mb-4">🔗</div>
          <div className="text-white font-bold text-lg mb-2">Link not found</div>
          <div className="text-slate-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const candidates = shortlist?.candidates || [];
  const respondedCount = Object.keys(feedback).length;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-slate-950/80 backdrop-blur-md sticky top-0 z-10">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-400">
              Candidate Shortlist
            </div>
            <h1 className="text-lg font-black text-white mt-0.5">
              {shortlist?.jobTitle || 'Shortlisted Candidates'}
            </h1>
            {shortlist?.clientName && (
              <div className="text-xs text-slate-400">Prepared for {shortlist.clientName}</div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-black text-white">{respondedCount}/{candidates.length}</div>
            <div className="text-micro uppercase tracking-[0.16em] text-slate-500">Reviewed</div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {candidates.length > 0 && (
        <div className="h-1 bg-slate-900">
          <div
            className="h-full bg-cyan-400 transition-all duration-500"
            style={{ width: `${(respondedCount / candidates.length) * 100}%` }}
          />
        </div>
      )}

      {/* Cards grid */}
      <div className="mx-auto max-w-5xl px-4 py-8">
        {allDone && (
          <div className="mb-8 rounded-card border border-emerald-400/30 bg-emerald-500/10 px-6 py-5 text-center">
            <div className="text-2xl mb-2">✅</div>
            <div className="text-sm font-bold text-emerald-300">All candidates reviewed — thank you!</div>
            <div className="text-xs text-slate-400 mt-1">Your feedback has been sent to the recruiter.</div>
          </div>
        )}

        {candidates.length === 0 ? (
          <div className="text-center py-20 text-slate-500">No candidates on this shortlist.</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {candidates.map((c) => (
              <CandidateCard
                key={c.id}
                candidate={c}
                feedback={feedback[c.id] || null}
                onFeedback={handleFeedback}
              />
            ))}
          </div>
        )}

        <div className="mt-12 text-center text-micro text-slate-600">
          Powered by Vision Recruitment · Feedback is visible to your recruiter only
        </div>
      </div>
    </div>
  );
}
